import React, { useCallback } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { Table, Typography } from 'antd'

import { THEME } from '../../../core/constants'
import {
  createUrl,
  getQueryParams,
} from '../../../core/utils/services/queryParams'

import { LondonDateTime } from '../../../common/components'
import {
  DEFAULT_PAGE,
  DISH_REVIEW_TASKS_PERMISSIONS,
  SORTING_ORDERS,
} from '../../../common/constants'
import {
  showErrorNotification,
  showSuccessNotification,
} from '../../../common/helpers'
import { usePaginationFixer } from '../../../common/hooks'
import { useDocumentTitle } from '../../../common/hooks/effects'
import { getCachedFilterForPage } from '../../../common/services'
import { getTableSortingOrder, tableOnRowClick } from '../../../common/utils'

import {
  DISH_REVIEW_TASK_SORTING_CRITERIA,
  MENU_STATUSES,
} from '../../../menus/constants'
import { DISH_REVIEW } from '../../constants'
import {
  ASSIGN_DISH_REVIEW_TASK,
  UNASSIGN_DISH_REVIEW_TASK,
} from '../../graphql/mutations'
import { DISH_REVIEW_TASKS } from '../../graphql/queries'

import TaskTableAssignedColumn from './sections/task-table-assigned-column'

const DishReviewTasksPage = () => {
  useDocumentTitle('Tasks - Dish Review')
  const history = useHistory()
  const { search, pathname } = useLocation()

  const {
    brandSearchTerm,
    menuSearchTerm,
    statuses,
    reopenReasons,
    resolvedType,
    isAssigned,
    assignedToId,
    isAwaiting,
    awaitingReason,
    menuFormats,
    menuStatuses,
    menuIsAwaitingScraping,
    menuScrapedById,
    menuScrapingTypes,
    isVerified,
    isRerunFlow,
    page = DEFAULT_PAGE,
    pageSize = 10,
    sortingCriterion,
    sortingOrder,
    brandId,
  } = getQueryParams(search)

  const {
    data: { dishReviewTasks = {} } = {},
    loading: isLoadingTasks,
    refetch: refetchTasks,
  } = useQuery(DISH_REVIEW_TASKS, {
    variables: {
      filters: {
        brandSearchTerm: brandSearchTerm && String(brandSearchTerm),
        menuSearchTerm: menuSearchTerm && String(menuSearchTerm),
        statuses,
        reopenReasons,
        resolvedType,
        isAssigned,
        assignedToId,
        isAwaiting,
        awaitingReason,
        menuFormats,
        menuStatuses,
        menuScrapingTypes,
        menuIsAwaitingScraping,
        menuScrapedById,
        isVerified,
        isRerunFlow,
        brandId,
      },
      page,
      pageSize,
      sortingCriterion,
      sortingOrder,
    },
    fetchPolicy: 'no-cache',
  })

  usePaginationFixer(isLoadingTasks, dishReviewTasks, 'dishReviewTasks')

  const [assignTask, { loading: isAssigningTask }] = useMutation(
    ASSIGN_DISH_REVIEW_TASK,
    {
      onCompleted() {
        refetchTasks()
        showSuccessNotification({
          message: 'Dish review task assigned.',
        })
      },

      onError({ message }) {
        showErrorNotification({
          message: 'Dish review task assign failed.',
          description: message,
        })
      },
    },
  )

  const [unassignTask, { loading: isUnassigningTask }] = useMutation(
    UNASSIGN_DISH_REVIEW_TASK,
    {
      onCompleted() {
        refetchTasks()
        showSuccessNotification({
          message: 'Dish review task unassigned.',
        })
      },
      onError({ message }) {
        showErrorNotification({
          message: 'Dish review task unassign failed.',
          description: message,
        })
      },
    },
  )

  const tableColumns = [
    {
      title: 'Brand ID',
      dataIndex: 'menu.brand.id',
      width: '100px',
    },
    {
      title: 'Menu ID',
      dataIndex: 'menu.id',
      width: '80px',
    },
    {
      title: 'Assigned',
      width: '140px',
      render: task => (
        <TaskTableAssignedColumn
          task={task}
          taskType={DISH_REVIEW}
          permissions={DISH_REVIEW_TASKS_PERMISSIONS}
          assignTask={assignTask}
          unassignTask={unassignTask}
          refetchTasks={refetchTasks}
        />
      ),
    },
    {
      title: 'Brand Name',
      width: '160px',
      dataIndex: 'menu.brand.name',
    },
    {
      title: 'Locations',
      dataIndex: 'menu.locationCount',
      width: '100px',
    },
    {
      title: 'Menu URL',
      width: '300px',
      dataIndex: 'menu.url',
      ellipsis: true,
      render: menuURL =>
        menuURL && (
          <a href={menuURL} target="_blank" rel="noopener noreferrer">
            {menuURL}
          </a>
        ),
    },
    {
      title: 'Menu status',
      width: '100px',
      dataIndex: 'menu.status',
      render: menuStatus => menuStatus && MENU_STATUSES[menuStatus].name,
    },
    {
      title: 'Updated at',
      dataIndex: 'modifiedDate',
      width: '160px',
      render: date => <LondonDateTime date={date} />,
      sorter: true,
    },
    {
      title: 'Scraped at',
      dataIndex: 'menu.scrapedDate',
      width: '160px',
      render: date => <LondonDateTime date={date} />,
      sorter: true,
      defaultSortOrder: SORTING_ORDERS.DESC.antd,
    },
  ]

  const redirectToDishReviewTask = useCallback(
    task =>
      history.push(getCachedFilterForPage(`/tasks/dish-review/${task.id}`)),
    [history],
  )

  const onRow = useCallback(
    row => ({
      onClick: event => tableOnRowClick(event, row, redirectToDishReviewTask),
      style: row.menu?.isAdvancedProbeEnabled
        ? { backgroundColor: THEME.colors.advancedProbeGreen }
        : undefined,
    }),
    [redirectToDishReviewTask],
  )

  const getSortingCriterion = (field, order) => {
    if (!order) return ''

    switch (field) {
      case 'modifiedDate':
        return DISH_REVIEW_TASK_SORTING_CRITERIA.MODIFIED_DATE
      case 'menu.scrapedDate':
        return DISH_REVIEW_TASK_SORTING_CRITERIA.MENU_SCRAPED_DATE
      default:
        return ''
    }
  }

  const handleTableChange = useCallback(
    (pagination, filters, sorter) => {
      const { order, field } = sorter

      const sortingOrder = getTableSortingOrder(order)
      const sortingCriterion = getSortingCriterion(field, order)

      history.push(
        createUrl(pathname, search, {
          page: pagination.current,
          sortingCriterion,
          sortingOrder,
        }),
      )
    },
    [history, search, pathname],
  )

  return (
    <>
      <Typography.Text>
        {isLoadingTasks
          ? 'Loading records'
          : `${dishReviewTasks.total} matching records`}
      </Typography.Text>
      <Table
        loading={isLoadingTasks || isAssigningTask || isUnassigningTask}
        rowKey="id"
        columns={tableColumns}
        dataSource={dishReviewTasks.dishReviewTasks}
        pagination={{
          total: dishReviewTasks.total,
        }}
        onChange={handleTableChange}
        onRow={onRow}
      />
    </>
  )
}

export default DishReviewTasksPage
